import {
  formatCents,
  formatDate,
  formatDuration,
  Permission,
  REQUEST_TYPE_LABELS,
  RequestStatus,
  type RequestSummaryDto,
} from '@i9portal/shared';
import { useQuery } from '@tanstack/react-query';
import { Link, Navigate } from 'react-router';
import { EmptyState } from '../../components/empty-state';
import { ErrorState } from '../../components/error-state';
import { KpiCard } from '../../components/kpi-card';
import { PageHeader } from '../../components/page-header';
import { PageSpinner } from '../../components/spinner';
import { StatusBadge } from '../../components/status-badge';
import { useDocumentTitle } from '../../hooks/use-document-title';
import { meApi } from '../../lib/api/endpoints/me';
import { queryKeys } from '../../lib/api/query-keys';
import { useAuth } from '../../lib/auth/use-auth';
import { employeeRequestPath } from '../../lib/requests';

function RequestRow({ item, showReason }: { item: RequestSummaryDto; showReason?: boolean }) {
  return (
    <li className="request-row">
      <div className="request-row__main">
        <Link to={employeeRequestPath(item.type, item.id)} className="request-row__code">
          {REQUEST_TYPE_LABELS[item.type]} {item.code}
        </Link>
        <span className="request-row__desc">{item.description}</span>
        <span className="muted">
          {formatDate(item.referenceDate)}
          {item.durationMinutes ? ` · ${formatDuration(item.durationMinutes)}` : ''}
          {item.amountCents !== null
            ? ` · ${formatCents(item.approvedAmountCents ?? item.amountCents)}`
            : ''}
        </span>
      </div>
      <StatusBadge status={item.status} paymentStatus={item.paymentStatus} />
      {showReason &&
        (item.status === RequestStatus.REJECTED ||
          item.status === RequestStatus.RETURNED_FOR_CORRECTION) && (
          <p className="request-row__hint">Abra a solicitação para ver o motivo e corrigir.</p>
        )}
    </li>
  );
}

export function HomePage() {
  useDocumentTitle('Início');
  const { me, can } = useAuth();
  const query = useQuery({
    queryKey: queryKeys.summary,
    queryFn: meApi.summary,
    enabled: !!me?.employee,
  });

  // Usuários sem cadastro de funcionário (RH/Admin) vão direto para sua área.
  if (!me?.employee) {
    if (can(Permission.DASHBOARD_READ)) return <Navigate to="/rh" replace />;
    if (can(Permission.USERS_READ)) return <Navigate to="/admin/usuarios" replace />;
    return <Navigate to="/perfil" replace />;
  }

  return (
    <div className="page">
      <PageHeader
        title={`Olá, ${me.name.split(' ')[0]}!`}
        description="Resumo das suas horas extras e reembolsos."
        actions={
          <>
            <Link className="btn btn--primary" to="/horas-extras/nova">
              Registrar hora extra
            </Link>
            <Link className="btn btn--secondary" to="/reembolsos/novo">
              Solicitar reembolso
            </Link>
          </>
        }
      />
      {query.isPending ? (
        <PageSpinner />
      ) : query.isError ? (
        <ErrorState error={query.error} onRetry={() => void query.refetch()} />
      ) : (
        <>
          <section aria-labelledby="home-overtime">
            <h2 className="section-title" id="home-overtime">
              Horas extras
            </h2>
            <div className="grid grid--4">
              <KpiCard
                label="Pendentes de análise"
                value={query.data.overtime.pendingCount}
                to="/horas-extras?status=UNDER_REVIEW"
                tone="info"
              />
              <KpiCard
                label="Aprovadas, a receber"
                value={formatCents(query.data.overtime.approvedUnpaidAmountCents)}
                hint={formatDuration(query.data.overtime.approvedUnpaidMinutes)}
                to="/horas-extras?status=APPROVED"
                tone="warning"
              />
              <KpiCard
                label="Pagas neste mês"
                value={formatCents(query.data.overtime.paidThisMonthAmountCents)}
                tone="success"
              />
              <KpiCard
                label="Horas no mês"
                value={formatDuration(query.data.overtime.monthMinutes)}
              />
            </div>
          </section>
          <section aria-labelledby="home-reimbursement">
            <h2 className="section-title" id="home-reimbursement">
              Reembolsos
            </h2>
            <div className="grid grid--3">
              <KpiCard
                label="Pendentes de análise"
                value={query.data.reimbursement.pendingCount}
                to="/reembolsos?status=UNDER_REVIEW"
                tone="info"
              />
              <KpiCard
                label="Aprovados, a receber"
                value={formatCents(query.data.reimbursement.approvedUnpaidAmountCents)}
                to="/reembolsos?status=APPROVED"
                tone="warning"
              />
              <KpiCard
                label="Pagos neste mês"
                value={formatCents(query.data.reimbursement.paidThisMonthAmountCents)}
                tone="success"
              />
            </div>
          </section>
          <div className="grid grid--2">
            <section className="card" aria-labelledby="home-attention">
              <h2 className="card__title" id="home-attention">
                Precisa da sua atenção
              </h2>
              {query.data.needsAttention.length === 0 ? (
                <EmptyState
                  title="Tudo em dia"
                  description="Nenhuma solicitação devolvida, recusada ou em rascunho."
                />
              ) : (
                <ul className="request-list">
                  {query.data.needsAttention.map((item) => (
                    <RequestRow key={`${item.type}-${item.id}`} item={item} showReason />
                  ))}
                </ul>
              )}
            </section>
            <section className="card" aria-labelledby="home-recent">
              <h2 className="card__title" id="home-recent">
                Recentes
              </h2>
              {query.data.recent.length === 0 ? (
                <EmptyState title="Nenhuma solicitação ainda" />
              ) : (
                <ul className="request-list">
                  {query.data.recent.map((item) => (
                    <RequestRow key={`${item.type}-${item.id}`} item={item} />
                  ))}
                </ul>
              )}
            </section>
          </div>
        </>
      )}
    </div>
  );
}
